import { useCallback, useState } from "react";
import { processError } from "../../../../common/utils/base_error";
import { useMaquinasEquipControlePagination } from "../hooks/MaquinasEquip_controle/pagination";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { MaquinasequipControleForm } from "../schemas/MaquinasEquip_controle/MaquinasEquip_controle";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { ControleCentroCustoLista } from "../../domain/models/controle_centro_custo_lista";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useMaquinasEquipControleFilters } from "../hooks/MaquinasEquip_controle/filter";

/**
 * LoadControleCentroCustoFuncState
 */

type LoadControleCentroCustoFuncState = {
  centroCusto?: number[] | null;
  status?: number | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useMaquinasEquipControleViewModel
 */

export const useMaquinasEquipControleViewModel = () => {
  const {
    controlePatrimonio: {
      getAllControlePatrimonioControleUseCase,
      createControlePatrimonioControleUseCase,
      updateControlePatrimonioControleUseCase,
      deleteControlePatrimonioControleUseCase,
    },
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
  } = useDropDownLoader();

  const backOrNavigate = useBackOrNavigate();

  const { pushErrorNotification, pushInfoNotification, setPageSaving } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [controleCentroCustos, setControleCentroCustos] = useState<ControleCentroCustoLista[]>();
  const [selectedControleCentroCusto, setSelectedControleCentroCusto] = useState<ControleCentroCustoLista>();

  const [controleCentroCustoModal, setControleCentroCustoModal] = useState(false);
  const [controleCentroCustoDeleteModal, setControleCentroCustoDeleteModal] = useState(false);

  const [centroCustoDropdown, setCentroCustoDropdown] = useState<CentroCustoDropDown[]>([]);

  const filters = useMaquinasEquipControleFilters();
  const pagination = useMaquinasEquipControlePagination();

  /**
   * Funcoes de carregamento
   */

  const loadControleCentroCusto = useCallback(async (fs?: LoadControleCentroCustoFuncState) => {
    try {
      const filterCentroCusto = evaluateParamValue(fs?.centroCusto, filters.centroCusto);
      const filterStatus = evaluateParamValue(fs?.status, filters.status);

      const filterPage = fs?.page ?? pagination.page;
      const filterRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllControlePatrimonioControleUseCase.getAllControlePatrimonioControle(
        filterCentroCusto,
        filterStatus === 2 ? null : filterStatus,
        filterPage,
        filterRowsPerPage,
      );

      setControleCentroCustos(getAllControlePatrimonioControleUseCase.items);
      pagination.setTotalItems(getAllControlePatrimonioControleUseCase.totalItems);
    } catch (e: unknown) {
      setControleCentroCustos([]);
      throw new Error("Falha ao buscar controle de patrimônio!");
    }
  }, [
    filters,
    pagination,
    getAllControlePatrimonioControleUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));
      await loadControleCentroCusto();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados do controle de patrimônio!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));
      await loadControleCentroCusto();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados do controle de patrimônio!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropDown,
    loadControleCentroCusto,
    pushErrorNotification,
  ]);

  const handleControleCentroCustoOpenState = useCallback((idControleCentroCusto: number | null, openState: boolean) => {
    if (openState) {
      if (idControleCentroCusto && controleCentroCustos) {
        setSelectedControleCentroCusto(controleCentroCustos.find(c => c.idControleCentroCusto === idControleCentroCusto));
      } else {
        setSelectedControleCentroCusto(undefined);
      }
    }
    setControleCentroCustoModal(openState);
  }, [
    controleCentroCustos,
  ]);

  const handleControleCentroCustoSubmit = useCallback(async (form: MaquinasequipControleForm) => {
    try {
      setSaving(true);
      setPageSaving(true);
      if (selectedControleCentroCusto) {
        await updateControlePatrimonioControleUseCase.updateControlePatrimonioControle(
          selectedControleCentroCusto.idControleCentroCusto,
          form.idCentroCusto,
        );
      } else {
        await createControlePatrimonioControleUseCase.createControlePatrimonioControle(
          form.idCentroCusto,
        );
      }
      await loadControleCentroCusto();
      setSelectedControleCentroCusto(undefined);
      setControleCentroCustoModal(false);
      pushInfoNotification("Sucesso", "Salvo Com Sucesso");
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar controle de patrimônio!"));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedControleCentroCusto,
    createControlePatrimonioControleUseCase,
    updateControlePatrimonioControleUseCase,
    loadControleCentroCusto,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleControleCentroCustoDeleteOpenState = useCallback((idControleCentroCusto: number | null, openState: boolean) => {
    if (openState) {
      if (idControleCentroCusto && controleCentroCustos) {
        setSelectedControleCentroCusto(controleCentroCustos.find(c => c.idControleCentroCusto === idControleCentroCusto));
      } else {
        setSelectedControleCentroCusto(undefined);
      }
    }
    setControleCentroCustoDeleteModal(openState);
  }, [
    controleCentroCustos,
  ]);

  const handleControleCentroCustoDeleteSubmit = useCallback(async () => {
    if (selectedControleCentroCusto) {
      try {
        setSaving(true);
        setPageSaving(true);
        await deleteControlePatrimonioControleUseCase.deleteControlePatrimonioControle(
          selectedControleCentroCusto.idControleCentroCusto,
        );
        await loadControleCentroCusto();
        setSelectedControleCentroCusto(undefined);
        setControleCentroCustoDeleteModal(false);
        pushInfoNotification("Sucesso", "Registro excluído com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao excluir controle de patrimônio!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedControleCentroCusto,
    deleteControlePatrimonioControleUseCase,
    loadControleCentroCusto,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useMaquinasEquipControlePagination.setState({ page: page + 1 });
      await loadControleCentroCusto({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadControleCentroCusto,
  ]);


  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useMaquinasEquipControlePagination.setState({ page: 1, rowsPerPage });
      await loadControleCentroCusto({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadControleCentroCusto,
  ]);

  const handleCentroCustoChange = useCallback(async (centroCusto: number[] | null) => {
    try {
      setLoading(true);
      useMaquinasEquipControleFilters.setState({ centroCusto });
      await loadControleCentroCusto({ centroCusto });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadControleCentroCusto,
    pushErrorNotification,
  ]);

  const handleStatusChange = useCallback(async (status: number | null) => {
    try {
      setLoading(true);
      useMaquinasEquipControleFilters.setState({ status });
      await loadControleCentroCusto({ status });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadControleCentroCusto,
    pushErrorNotification,
  ]);


  const handleClear = useCallback(async () => {
    try {
      setLoading(true);
      useMaquinasEquipControleFilters.setState({ centroCusto: null });
      useMaquinasEquipControlePagination.setState({ page: 1 });
      await loadControleCentroCusto({ centroCusto: null, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao atualizar página!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadControleCentroCusto,
    pushErrorNotification,
  ]);

  const handleViewItens = useCallback((idChecklist: number | null, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/MaquinasEquipItens_controle?id=${idChecklist}`, "_blank", "noopener,noreferrer");
    } else {
      backOrNavigate(`/MaquinasEquipItens_controle?id=${idChecklist}`);
    }
  }, [
    backOrNavigate
  ]);

  return {
    loading,
    saving,
    errorMessage,
    controleCentroCusto: controleCentroCustos ?? [],
    selectedControleCentroCusto: selectedControleCentroCusto ?? null,
    controleCentroCustoModal,
    controleCentroCustoDeleteModal,
    centroCustoDropdown: centroCustoDropdown ?? [],
    filters,
    pagination,
    handleReload,
    handleControleCentroCustoOpenState,
    handleControleCentroCustoSubmit,
    handleControleCentroCustoDeleteOpenState,
    handleControleCentroCustoDeleteSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleClear,
    handleCentroCustoChange,
    handleStatusChange,
    handleViewItens,
  };
};
